import React, { useEffect, useMemo, useRef } from 'react'
import { useSpreadCheckpoint } from '@/api/hooks/use-spread-checkpoint'
import type { PhotoCandid } from '@/api/types/photo'
import type { ClikArtItem } from '@/api/types/clikart'
import { readStoredEditorZoom } from '@/lib/zoom/editor-zoom-storage'
import { readStoredSpreadIndex } from '@/lib/editor/editor-spread-storage'
import { EDITOR_ZOOM_MAX, EDITOR_ZOOM_MIN } from '@/lib/zoom/editor-zoom'
import {
  sortSpreads,
  spreadPagesForMenubarIndicator,
  useSpreadsQuery,
} from '@/api/hooks/use-spreads'
import { useEmbedSession } from '@/contexts/embed-session-context'
import { isDevLandingPageOverrideConfigured } from '../../helpers/dev-landing-page'

/**
 * Zoom state (stored, effective, toolbar key flash, fit availability), the active spread and its
 * checkpoint query, the zoom screen-reader announcement policy, and the photo / ClikArt placement
 * warning dialog state.
 *
 * Embedded in a host's iframe (`useEmbedSession`), the active spread is the host's spread, found by
 * id, and spread navigation is locked (`spreadNavigationLocked`); the stored index is ignored.
 */
export function useHomeZoomAndSpread() {
  const [zoom, setZoom] = React.useState(() => readStoredEditorZoom() ?? 1)
  /** Live scale when `zoom === 0` (fit-to-page), from CenterCanvas — for zoom-in limits and toolbar state. */
  const [effectiveCanvasZoom, setEffectiveCanvasZoom] = React.useState(1)
  const effectiveCanvasZoomRef = React.useRef(1)

  effectiveCanvasZoomRef.current = effectiveCanvasZoom

  const [fitToSpreadViewportOk, setFitToSpreadViewportOk] = React.useState(true)

  const handleFitToSpreadAvailabilityChange = React.useCallback((ok: boolean) => {
    setFitToSpreadViewportOk(ok)
  }, [])

  const effectiveEditorZoom = zoom === 0 ? effectiveCanvasZoom : zoom
  const effectiveEditorZoomRef = React.useRef(effectiveEditorZoom)

  effectiveEditorZoomRef.current = effectiveEditorZoom

  const canZoomIn = +effectiveEditorZoom.toFixed(3) < EDITOR_ZOOM_MAX
  const canZoomOut = +effectiveEditorZoom.toFixed(3) > EDITOR_ZOOM_MIN

  const [zoomToolbarKeyFlash, setZoomToolbarKeyFlash] = React.useState<'in' | 'out' | null>(null)
  const zoomToolbarKeyFlashTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    return () => {
      if (zoomToolbarKeyFlashTimerRef.current) {
        clearTimeout(zoomToolbarKeyFlashTimerRef.current)
      }
    }
  }, [])

  const flashZoomToolbarFromKeyboard = React.useCallback((kind: 'in' | 'out') => {
    if (zoomToolbarKeyFlashTimerRef.current) {
      clearTimeout(zoomToolbarKeyFlashTimerRef.current)
    }

    setZoomToolbarKeyFlash(kind)
    zoomToolbarKeyFlashTimerRef.current = setTimeout(() => {
      setZoomToolbarKeyFlash(null)
      zoomToolbarKeyFlashTimerRef.current = null
    }, 150)
  }, [])

  const embedSession = useEmbedSession()
  const embeddedSpreadId = embedSession?.spreadId ?? null
  const spreadNavigationLocked = embeddedSpreadId !== null

  const [storedSpreadIndex, setSpreadIndex] = React.useState(() =>
    spreadNavigationLocked ? 0 : (readStoredSpreadIndex() ?? 0)
  )
  // Embedded, this still runs the one-time load effect (`useHomeZoomCommands`) so the spread is
  // centered, but that effect leaves the index alone.
  const isRestoringSpread = React.useRef(
    spreadNavigationLocked ||
      isDevLandingPageOverrideConfigured() ||
      readStoredSpreadIndex() !== null
  )

  const { data: spreadsRaw } = useSpreadsQuery()
  const orderedSpreads = useMemo(() => sortSpreads(spreadsRaw ?? []), [spreadsRaw])
  const embeddedSpreadIndex = useMemo(
    () =>
      embeddedSpreadId === null
        ? -1
        : orderedSpreads.findIndex((spread) => String(spread.spreadId) === embeddedSpreadId),
    [embeddedSpreadId, orderedSpreads]
  )
  const spreadIndex = spreadNavigationLocked ? Math.max(embeddedSpreadIndex, 0) : storedSpreadIndex
  const activeSpread = spreadNavigationLocked
    ? (orderedSpreads[embeddedSpreadIndex] ?? null)
    : (orderedSpreads[spreadIndex] ?? null)
  const spreadId = activeSpread?.spreadId ?? ''
  const spreadIdRef = useRef('')

  useEffect(() => {
    spreadIdRef.current = spreadId
  }, [spreadId])

  // Hoisted ahead of useHomeEditorSettings' effects (guide/view/snap/restorable settings) so
  // they can gate their fetches on `isSpreadCheckpointLoaded` — those sections live inside the
  // same backend checkpoint cache entry that decompose populates, so fetching them before the
  // checkpoint call succeeds races the cache being built and 500s (CACHE_NOT_FOUND).
  const {
    data,
    error,
    isSuccess: isSpreadCheckpointLoaded,
  } = useSpreadCheckpoint(spreadId, { enabled: spreadId !== '' })

  // Deliberately NOT cleared on spread switch — photo-placement overrides are tracked per spread
  // (see `photo-placement-session.ts`) precisely so a photo placed here still shows "Placed" in
  // the tray/search after navigating away. Cleared at logout instead (`handleLogout`), since a
  // fresh login gets fresh server-computed placement data from the backend's book-wide scan.

  const lastAnnouncedZoomPercentRef = React.useRef<number | null>(null)
  const prevSpreadIdForZoomAnnounceRef = React.useRef(spreadId)
  const [zoomAriaAnnouncement, setZoomAriaAnnouncement] = React.useState('')
  const [photoPlacementAnnouncement, setPhotoPlacementAnnouncement] = React.useState('')
  const [photoPlacementAriaKey, setPhotoPlacementAriaKey] = React.useState(0)
  const [photoSizeWarningState, setPhotoSizeWarningState] = React.useState<{
    scenario: 'drop'
    photo: PhotoCandid
    /** Original target shape/text-box's own center — legacy centers the replacement frame here. */
    targetCenter: { canvasX: number; canvasY: number }
    intrinsicSize: { width: number; height: number } | null
  } | null>(null)
  const [textToImageWarningState, setTextToImageWarningState] = React.useState<{
    shapeId: string
    photo: PhotoCandid
    intrinsicSize: { width: number; height: number } | null
  } | null>(null)
  const [clikArtSizeWarningState, setClikArtSizeWarningState] = React.useState<{
    scenario: 'drop'
    item: ClikArtItem
    targetCenter: { canvasX: number; canvasY: number }
    intrinsicSize: { width: number; height: number } | null
  } | null>(null)
  const [clikArtTextToImageWarningState, setClikArtTextToImageWarningState] = React.useState<{
    shapeId: string
    item: ClikArtItem
    intrinsicSize: { width: number; height: number } | null
  } | null>(null)
  /** Bumped when live text changes so SRs re-announce if the same percent string repeats. */
  const [zoomAriaLiveKey, setZoomAriaLiveKey] = React.useState(0)
  /** When set by toolbar/keyboard zoom in|out, SR message uses "zoomed in/out"; TTL avoids stale labels after other zoom gestures. */
  const pendingZoomToolbarIntentRef = React.useRef<{
    kind: 'in' | 'out'
    expiresAt: number
  } | null>(null)
  const ZOOM_TOOLBAR_INTENT_TTL_MS = 1200

  type ZoomSrIntentDirection = 'in' | 'out'

  type ZoomScreenReaderAction =
    | { type: 'armToolbarIntent'; direction: ZoomSrIntentDirection }
    | { type: 'clearToolbarIntent' }
    | { type: 'resetAnnounceState' }
    | { type: 'seedLastAnnouncedSilently'; pct: number }
    | { type: 'announceToolbarShellCommit'; direction: ZoomSrIntentDirection; nextPct: number }
    | { type: 'announceDebouncedAfterZoomChange'; nextPct: number }

  /** Single policy for zoom SR strings, `pendingZoomToolbarIntentRef`, and `lastAnnouncedZoomPercentRef`. */
  const recordZoomScreenReader = React.useCallback((action: ZoomScreenReaderAction) => {
    const pushAnnouncement = (message: string, pct: number) => {
      lastAnnouncedZoomPercentRef.current = pct
      setZoomAriaLiveKey((k) => k + 1)
      setZoomAriaAnnouncement(message)
    }

    switch (action.type) {
      case 'armToolbarIntent':
        pendingZoomToolbarIntentRef.current = {
          kind: action.direction,
          expiresAt: Date.now() + ZOOM_TOOLBAR_INTENT_TTL_MS,
        }
        break
      case 'clearToolbarIntent':
        pendingZoomToolbarIntentRef.current = null
        break
      case 'resetAnnounceState':
        lastAnnouncedZoomPercentRef.current = null
        pendingZoomToolbarIntentRef.current = null
        break
      case 'seedLastAnnouncedSilently':
        lastAnnouncedZoomPercentRef.current = action.pct
        break
      case 'announceToolbarShellCommit':
        pendingZoomToolbarIntentRef.current = null
        pushAnnouncement(
          action.direction === 'in'
            ? `Canvas zoomed in to ${action.nextPct}%`
            : `Canvas zoomed out to ${action.nextPct}%`,
          action.nextPct
        )
        break
      case 'announceDebouncedAfterZoomChange': {
        const { nextPct: latestPct } = action

        if (latestPct === lastAnnouncedZoomPercentRef.current) return

        const pending = pendingZoomToolbarIntentRef.current

        pendingZoomToolbarIntentRef.current = null

        const useToolbarIntent =
          pending != null && Date.now() < pending.expiresAt && pending.kind != null

        const message =
          useToolbarIntent && pending.kind === 'in'
            ? `Canvas zoomed in to ${latestPct}%`
            : useToolbarIntent && pending.kind === 'out'
              ? `Canvas zoomed out to ${latestPct}%`
              : `Canvas zoomed to ${latestPct}%`

        pushAnnouncement(message, latestPct)
        break
      }
    }
  }, [])

  const hasSpreadsList = spreadsRaw !== undefined
  const navigationMaxIndex = useMemo(
    () => Math.max(0, orderedSpreads.length - 1),
    [orderedSpreads.length]
  )
  const spreadPageIndicator = useMemo(
    () => spreadPagesForMenubarIndicator(activeSpread),
    [activeSpread]
  )

  return {
    spreadsRaw,
    data,
    error,
    isSpreadCheckpointLoaded,
    zoom,
    setZoom,
    effectiveCanvasZoom,
    setEffectiveCanvasZoom,
    fitToSpreadViewportOk,
    setFitToSpreadViewportOk,
    zoomToolbarKeyFlash,
    setZoomToolbarKeyFlash,
    spreadIndex,
    setSpreadIndex,
    zoomAriaAnnouncement,
    setZoomAriaAnnouncement,
    photoPlacementAnnouncement,
    setPhotoPlacementAnnouncement,
    photoPlacementAriaKey,
    setPhotoPlacementAriaKey,
    photoSizeWarningState,
    setPhotoSizeWarningState,
    textToImageWarningState,
    setTextToImageWarningState,
    clikArtSizeWarningState,
    setClikArtSizeWarningState,
    clikArtTextToImageWarningState,
    setClikArtTextToImageWarningState,
    zoomAriaLiveKey,
    setZoomAriaLiveKey,
    effectiveCanvasZoomRef,
    handleFitToSpreadAvailabilityChange,
    effectiveEditorZoom,
    effectiveEditorZoomRef,
    canZoomIn,
    canZoomOut,
    zoomToolbarKeyFlashTimerRef,
    flashZoomToolbarFromKeyboard,
    isRestoringSpread,
    orderedSpreads,
    activeSpread,
    spreadId,
    spreadIdRef,
    lastAnnouncedZoomPercentRef,
    prevSpreadIdForZoomAnnounceRef,
    pendingZoomToolbarIntentRef,
    ZOOM_TOOLBAR_INTENT_TTL_MS,
    recordZoomScreenReader,
    hasSpreadsList,
    navigationMaxIndex,
    spreadPageIndicator,
    embedSession,
    spreadNavigationLocked,
  }
}
